import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Tooltip, toast } from "@heroui/react";
import { HelpCircle, Search } from "lucide-react";
import { useSubscribedBlacklists } from "../blacklist/use-blacklist";
import {
  BlacklistHitCard,
  DelegationOrderCard,
  OfficalBlacklistCard,
  TemplateStatsCard,
  UserInfoCard,
} from "./QueryCards";
import { SubscribedBlacklists, UnavailableBlacklists } from "./SubscribedBlacklists";

export type TrustedQueryPanelProps = {
  search?: string;
  onSearchChange: (uid: string) => void;
  isAuthenticated: boolean;
  userInfoUid?: string;
  onLoginRequired: () => void;
  onNavigateSelfBlacklist: () => void;
  onNavigatePublicBlacklist: (id: string, searchUid?: string) => void;
  /** 渲染收货历史区域；web 可传 Timeline，webview 可传 FlatList */
  renderHistory?: (uid: string, queryToken: number) => React.ReactNode;
  className?: string;
};

export function TrustedQueryPanel({
  search,
  onSearchChange,
  isAuthenticated,
  userInfoUid,
  onLoginRequired,
  onNavigateSelfBlacklist,
  onNavigatePublicBlacklist,
  renderHistory,
  className,
}: TrustedQueryPanelProps) {
  const [uidInput, setUidInput] = useState("");
  const [queryToken, setQueryToken] = useState(0);

  const queryUid = search ?? "";

  useEffect(() => {
    setUidInput(search ?? "");
  }, [search]);

  // 与「我订阅的黑名单」页共用同一缓存，不会各拉一次
  const subscribedQuery = useSubscribedBlacklists(isAuthenticated);
  const subscribedLoading = isAuthenticated && subscribedQuery.isPending;
  const subscribedList = useMemo(() => {
    const list = subscribedQuery.data ?? [];
    return {
      activeList: list.filter((item) => item.available),
      unavailableList: list.filter((item) => !item.available),
    };
  }, [subscribedQuery.data]);

  const handleSearch = useCallback(
    (selfCheck: boolean = false) => {
      const uid = uidInput.trim();
      if (!uid && !selfCheck) {
        toast.danger("请输入用户 UID");
        return;
      }
      if (selfCheck) {
        onSearchChange(userInfoUid ?? "");
      } else {
        onSearchChange(uid);
      }
      setQueryToken((prev) => prev + 1);
    },
    [uidInput, onSearchChange, userInfoUid],
  );

  if (!isAuthenticated) {
    return (
      <div className={`flex flex-col items-center space-y-6 ${className ?? ""}`}>
        <div className="py-12 text-center text-gray-500">您需要先登录才可访问此功能</div>
        <Button variant="primary" onPress={onLoginRequired}>
          登录
        </Button>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className ?? ""}`}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search
            size={18}
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-gray-400"
          />
          <Input
            placeholder="输入用户 UID"
            value={uidInput}
            onChange={(e) => setUidInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="w-full rounded-full pl-9"
          />
        </div>
        <Button variant="tertiary" onPress={() => handleSearch(true)} className="rounded-full">
          一键自查
        </Button>
        <Button variant="primary" onPress={() => handleSearch()} className="rounded-full">
          查询
        </Button>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-sm leading-6 font-medium text-gray-700">匹配来源</span>
        <SubscribedBlacklists
          items={subscribedList.activeList}
          loading={subscribedLoading}
          onNavigateSelfBlacklist={onNavigateSelfBlacklist}
          onNavigatePublicBlacklist={onNavigatePublicBlacklist}
        />
        {subscribedList.unavailableList.length > 0 && (
          <>
            <span className="mt-2 flex items-center text-sm leading-6 font-medium text-gray-700">
              不可用黑名单
              <Tooltip>
                <Tooltip.Trigger>
                  <HelpCircle className="ml-1 inline-block text-gray-400" size={16} />
                </Tooltip.Trigger>
                <Tooltip.Content>
                  部分黑名单因分享人取消了分享或其他原因导致不可用，您可以将其移除
                </Tooltip.Content>
              </Tooltip>
            </span>
            <UnavailableBlacklists
              items={subscribedList.unavailableList}
              loading={subscribedLoading}
            />
          </>
        )}
      </div>

      {queryUid && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <UserInfoCard uid={queryUid} queryToken={queryToken} />
            <TemplateStatsCard uid={queryUid} queryToken={queryToken} />
            <DelegationOrderCard uid={queryUid} queryToken={queryToken} />
            <BlacklistHitCard
              uid={queryUid}
              queryToken={queryToken}
              currentUserUid={userInfoUid}
              onNavigateSelfBlacklist={onNavigateSelfBlacklist}
              onNavigatePublicBlacklist={(id, searchUid) =>
                onNavigatePublicBlacklist(String(id), searchUid)
              }
            />
            <OfficalBlacklistCard uid={queryUid} queryToken={queryToken} />
          </div>
          {renderHistory && (
            <div className="rounded-none border border-gray-100 bg-white p-5 shadow-sm">
              <div className="mb-4 flex flex-col items-start justify-between sm:flex-row sm:items-center">
                <h4 className="text-md font-semibold text-gray-800">近期收货历史</h4>
                <p className="text-xs text-gray-500">测试期间，最多仅展示500条数据</p>
              </div>
              {renderHistory(queryUid, queryToken)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TrustedQueryPanel;
